import { IStairConfig, IStepGeometry, IStringerGeometry, IVec3 } from '@stair-craft/common/util';

import { STRINGER_THICKNESS, STRINGER_WIDTH, TETIVA_PROTRUSION } from '../../constants';
import { RESIDENTIAL_NORMS } from '../../norms';
import { buildFlightSawtoothX } from '../l-shape-build-flight-sawtooth-x/build-flight-sawtooth-x';
import { buildFlightSawtoothZ } from '../l-shape-build-flight-sawtooth-z/build-flight-sawtooth-z';
import { kosourTopLine, riserSeatOf } from '../shared-kosour-top-line/kosour-top-line';
import { tetivaBoard } from '../shared-tetiva-board/tetiva-board';

/**
 * Left/right stringers for flight 1 (before the turn). Extracted from `generateStringers` to keep its complexity low.
 */
function buildFlight1Stringers(
    flight1Steps: IStepGeometry[],
    sides: ('left' | 'right')[],
    config: IStairConfig,
    riserHeight: number,
    turnStartZ: number,
    kosourBottom: { rise: number; run: number } | undefined
): IStringerGeometry[] {
    const result: IStringerGeometry[] = [];
    const f1First: IStepGeometry = flight1Steps[0];
    const lastStep: IStepGeometry = flight1Steps[flight1Steps.length - 1];
    const f1EndY: number = lastStep.position.y + riserHeight;

    for (const side of sides) {
        const x: number = side === 'left' ? 0 : config.stairWidth;
        if (!kosourBottom) {
            result.push(
                tetivaBoard({
                    side,
                    riserHeight,
                    origin: { x, z: f1First.position.z },
                    direction: { x: 0, z: 1 },
                    firstTop: f1First.position.y,
                    treadDepth: f1First.treadDepth,
                    noseLength: config.noseLength,
                    from: -config.noseLength,
                    to: turnStartZ - f1First.position.z,
                    maxY: config.floorHeight + config.floorThickness,
                })
            );
            continue;
        }
        const points: IVec3[] = kosourTopLine(
            buildFlightSawtoothZ(flight1Steps, x, 0, f1EndY, turnStartZ),
            config.treadThickness,
            false,
            riserSeatOf(config)
        );
        result.push({ side, points, thickness: STRINGER_THICKNESS, width: STRINGER_WIDTH, bottomSlope: kosourBottom });
    }

    return result;
}

/**
 * Per-side world Z for a flight-2 stringer. Extracted from `generateStringers` to keep its complexity low.
 */
function computeFlight2Z(side: 'left' | 'right', isRight: boolean, turnStartZ: number, sw: number): number {
    if (isRight) {
        return side === 'left' ? turnStartZ + sw : turnStartZ;
    }
    return side === 'left' ? turnStartZ : turnStartZ + sw;
}

/**
 * Left/right stringers for flight 2 (after the turn), both turn directions. Extracted from `generateStringers` to keep its complexity low.
 */
function buildFlight2Stringers(
    flight2Steps: IStepGeometry[],
    sides: ('left' | 'right')[],
    config: IStairConfig,
    turnStartZ: number,
    riserHeight: number,
    kosourBottom: { rise: number; run: number } | undefined
): IStringerGeometry[] {
    const result: IStringerGeometry[] = [];
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    const isKosour: boolean = kosourBottom !== undefined;
    const maxY: number = config.floorHeight + config.floorThickness;
    const treadThickness: number = config.treadThickness;
    const f2First: IStepGeometry = flight2Steps[0];
    const f2Last: IStepGeometry = flight2Steps[flight2Steps.length - 1];
    const f2StartY: number = f2First.position.y;
    const f2EndY: number = Math.min(f2Last.position.y + riserHeight, maxY);
    const f2TreadDepth: number = f2First.treadDepth;
    const f2EntryY: number = Math.max(0, f2StartY - riserHeight);

    const startX: number = isRight ? sw : 0;
    const endX: number = isRight ? sw + flight2Steps.length * f2TreadDepth : -(flight2Steps.length * f2TreadDepth);
    const dir: 1 | -1 = isRight ? 1 : -1;

    for (const side of sides) {
        const z: number = computeFlight2Z(side, isRight, turnStartZ, sw);
        // The board body lies on the stair side of its edge: the near edge (turnStartZ) is
        // 'left' (+Z inward), the far edge 'right'.
        const inwardSide: 'left' | 'right' = z === turnStartZ ? 'left' : 'right';
        if (!isKosour) {
            result.push(
                tetivaBoard({
                    riserHeight,
                    maxY,
                    side: inwardSide,
                    origin: { x: startX, z },
                    direction: { x: dir, z: 0 },
                    firstTop: f2StartY,
                    treadDepth: f2TreadDepth,
                    noseLength: f2First.noseLength,
                    // The inner-corner board starts past flight 1's board, which reaches
                    // TETIVA_PROTRUSION beyond the turn edge.
                    from: z === turnStartZ ? TETIVA_PROTRUSION : 0,
                    to: Math.abs(endX - startX),
                })
            );
            continue;
        }
        // Flight 2 follows after the turn; the entry riser is owned by the previous segment
        // (winder/landing exit riser). Drop the entry point so the kosour starts at the first tread.
        const sawtooth: IVec3[] = buildFlightSawtoothX(
            flight2Steps.length,
            f2TreadDepth,
            startX,
            dir,
            z,
            f2EntryY,
            f2EndY,
            riserHeight
        ).slice(1);
        const points: IVec3[] = kosourTopLine(sawtooth, treadThickness, true, riserSeatOf(config));
        result.push({ points, side: inwardSide, thickness: STRINGER_THICKNESS, width: STRINGER_WIDTH, bottomSlope: kosourBottom });
    }

    return result;
}

export function generateStringers(
    flight1Steps: IStepGeometry[],
    flight2Steps: IStepGeometry[],
    config: IStairConfig,
    riserHeight: number,
    turnStartZ: number
): IStringerGeometry[] {
    const stringers: IStringerGeometry[] = [];
    const isKosour: boolean = config.stringerType === 'kosour';
    // Use IDEAL tread depth for vertical board thickness — independent of the force-fit treadDepth,
    // so the kosour board doesn't become visually too thick when the stair angle is steep.
    const kosourBottom: { rise: number; run: number } | undefined = isKosour
        ? { rise: riserHeight, run: RESIDENTIAL_NORMS.treadDepthIdeal }
        : undefined;

    const sides: ('left' | 'right')[] = ['left', 'right'];

    if (flight1Steps.length > 0) {
        stringers.push(...buildFlight1Stringers(flight1Steps, sides, config, riserHeight, turnStartZ, kosourBottom));
    }

    if (flight2Steps.length > 0) {
        stringers.push(...buildFlight2Stringers(flight2Steps, sides, config, turnStartZ, riserHeight, kosourBottom));
    }

    return stringers;
}
